/**
 * @file A component that optimizes the loading of
 * thrid-party scripts. Borrowed from `next/script`
 */

import { useEffect, useId, useRef } from "react";

import { logger } from "../../utils/logging";

import type { ScriptHTMLAttributes } from "react";

export interface ScriptProps extends ScriptHTMLAttributes<HTMLScriptElement> {
  children?: never;
  id?: string;
  type?: string;
  onError?: (e: any) => void;
  onLoad?: (e: any) => void;
  onReady?: () => void;
}

const ScriptCache = new Map();
const LoadCache = new Set();

const load = (props: ScriptProps): void => {
  const { dangerouslySetInnerHTML, id, src, type, onError, onLoad } = props;

  const cacheKey = id || src;

  // Script has already loaded
  if (cacheKey && LoadCache.has(cacheKey)) {
    return;
  }

  // Contents of this script are already loading/loaded
  if (ScriptCache.has(src)) {
    LoadCache.add(cacheKey);
    ScriptCache.get(src).then(onLoad, onError);
    return;
  }

  const el = document.createElement("script");

  const afterLoad = () => {
    // Add cacheKey to LoadCache when load successfully
    LoadCache.add(cacheKey);
  };

  const loadPromise = new Promise<void>((resolve, reject) => {
    el.addEventListener("load", function (e) {
      resolve();
      onLoad?.(e);
      afterLoad();
    });
    el.addEventListener("error", function (e) {
      reject(e);
    });
  }).catch(function (e) {
    onError?.(e);
    logger.error("Script load error", e);
  });

  if (dangerouslySetInnerHTML) {
    el.innerHTML = (dangerouslySetInnerHTML.__html as string) || "";
    afterLoad();
  } else if (src) {
    el.src = src;
    el.type = type || "text/javascript";

    // cacheKey will be added to LoadCache when it is
    // actually loaded (see loadPromise above)
    ScriptCache.set(src, loadPromise);
  }

  document.body.appendChild(el);
};

/** Optimizes load of third-party scripts   */
export function LoadScript(props: ScriptProps) {
  const hasLoadScriptEffectCalled = useRef(false);
  const id = useId();

  useEffect(() => {
    if (!hasLoadScriptEffectCalled.current) {
      load({ id, ...props });

      hasLoadScriptEffectCalled.current = true;
    }
  }, [id, props]);

  return null;
}
